import {isUndefined} from 'lodash';
import {timingFunctions} from 'polished';
import React, {FC, memo, ReactNode} from 'react';
import styled from 'styled-components';

import {buildAccessibleButtonProps} from '../../../../libs/shared/core/src/helpers/accessibility/accessibilityHelpers';
import {
  buildInteractionIdProps,
  LegacyInteractionContextValue,
} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {InteractionTypesEnum} from '../../../../libs/shared/core/src/helpers/interaction/interactionTypes';
import {FrontClickEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {GridItem} from '../../../../libs/shared/ui/src/components/grid/grid';
import {useLegacyInteractionContext} from '../../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {
  TrackComponentInteraction,
  useInteractionTracking,
} from '../../../../libs/shared/ui/src/interactionTracking/interactionTrackingContext';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {SegmentedFilterBadge, SegmentedFilterBadgeProps} from './segmentedFilterBadge';

/*
 * Props.
 */

export interface FilterDefinition {
  id: string;
  content: ReactNode;
  total?: number;
  onClick?: () => void;
}

interface SegmentedFiltersProps {
  filters: ReadonlyArray<FilterDefinition>;
  selectedFilterIndex: number;
  color?: string;
  searchQuery?: string;
}

/*
 * Style.
 */

const StyledFiltersDiv = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;

  background-color: ${(p) => p.theme.greys.shade20};
  border-radius: 100px;
`;

const StyledFilterGridItem = styled(GridItem)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const StyledLabelDiv = styled.div`
  padding: 4px 0;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
`;

const StyledSelectedLabelDiv = styled(StyledLabelDiv)`
  color: ${(p) => p.theme.greys.white};
`;

// Common transition for the animated elements (easeOutQuint).
const transition = `0.3s ${timingFunctions('easeOutQuint')}`;

interface FiltersSelectedStyleProps {
  $selectedFilterIndex: number;
}
const StyledFiltersSelectedDiv = styled.div<FiltersSelectedStyleProps>`
  grid-column: 1;
  grid-row: 1;
  pointer-events: none;

  position: relative;
  overflow: hidden;
  border-radius: 100px;

  transform: translateX(${(p) => p.$selectedFilterIndex * 100}%);
  transition: transform ${transition};
`;

interface FiltersSelectedContentStyleProps {
  $selectedFilterIndex: number;
  $filtersCount: number;
  $color?: string;
}
const StyledFiltersSelectedContentDiv = styled.div<FiltersSelectedContentStyleProps>`
  position: absolute;
  width: ${(p) => p.$filtersCount * 100}%;

  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  background-color: ${(p) => p.$color || p.theme.palette.blue.shade40};

  transform: translateX(-${(p) => p.$selectedFilterIndex * (100 / p.$filtersCount)}%);
  transition: transform ${transition};
`;

/*
 * Component.
 */

export const SegmentedFilters: FC<SegmentedFiltersProps> = (props) => {
  const interactionContext = useLegacyInteractionContext();
  const {trackComponentInteraction} = useInteractionTracking();

  return (
    <StyledFiltersDiv>
      {renderFilters(props, interactionContext, trackComponentInteraction)}
      <StyledFiltersSelectedDiv $selectedFilterIndex={props.selectedFilterIndex}>
        <StyledFiltersSelectedContentDiv
          $selectedFilterIndex={props.selectedFilterIndex}
          $color={props.color}
          $filtersCount={props.filters.length}
        >
          {renderSelectedFilters(props)}
        </StyledFiltersSelectedContentDiv>
      </StyledFiltersSelectedDiv>
    </StyledFiltersDiv>
  );
};

/** Render the static filters in the background. */
function renderFilters(
  {filters, selectedFilterIndex, searchQuery}: SegmentedFiltersProps,
  interactionContext: LegacyInteractionContextValue,
  trackComponentInteraction: TrackComponentInteraction,
) {
  return filters.map((filter, index) => {
    const interactionProps = {
      interactionId: `filter-${filter.id}`,
    };

    function onClick(event: FrontClickEvent) {
      if (filter.onClick) {
        filter.onClick();
      }

      trackComponentInteraction(InteractionTypesEnum.CLICK, interactionContext, interactionProps, event);
    }

    return (
      <StyledFilterGridItem
        key={filter.id}
        column={index + 1}
        {...buildAccessibleButtonProps({onClick})}
        {...buildInteractionIdProps(interactionProps.interactionId)}
      >
        <StyledLabelDiv>{filter.content}</StyledLabelDiv>
        {maybeRenderFilterTotal(filter.total, selectedFilterIndex === index, searchQuery)}
      </StyledFilterGridItem>
    );
  });
}

/** Render the selected filters in the foreground. */
function renderSelectedFilters({filters, selectedFilterIndex, searchQuery}: SegmentedFiltersProps) {
  return filters.map((filter, index) => (
    <StyledFilterGridItem key={filter.id} column={index + 1}>
      <StyledSelectedLabelDiv>{filter.content}</StyledSelectedLabelDiv>
      {maybeRenderFilterTotal(filter.total, selectedFilterIndex === index, searchQuery)}
    </StyledFilterGridItem>
  ));
}

/**
 * We should re-render one filter badge if:
 *  - selected filter has changed OR
 *  - selected filter has not changed but its value was defined and has changed OR
 *  - selected filter has not changed but the search query has changed.
 */
const MemoizedFilterBadge = memo<SegmentedFilterBadgeProps & {searchQuery?: string}>(
  SegmentedFilterBadge,
  (prevProps, nextProps) => {
    const hasSelectedFilterChanged = prevProps.isSelectedFilter !== nextProps.isSelectedFilter;

    const hasValueChanged =
      (isUndefined(prevProps.value) && !isUndefined(nextProps.value)) ||
      (!isUndefined(prevProps.value) && !isUndefined(nextProps.value) && prevProps.value !== nextProps.value);

    const hasSearchQueryChanged = prevProps.searchQuery !== nextProps.searchQuery;

    const shouldRerender = hasSelectedFilterChanged || hasValueChanged || hasSearchQueryChanged;

    return !shouldRerender;
  },
);

function maybeRenderFilterTotal(total: number | undefined, isSelectedFilter: boolean, searchQuery?: string) {
  if (!searchQuery && !total) {
    return null;
  }

  // Render the badge.
  return <MemoizedFilterBadge isSelectedFilter={isSelectedFilter} value={total} searchQuery={searchQuery} />;
}
